/**
 * App.tsx — the root.
 * Renders: Auth screen OR Workspace shell.
 * Per app-codex: this is the surface "app.shell".
 *
 * Dev mode: when `useApp.dev === true`, skip auth and use local seed data.
 * The "current member" defaults to "Sarah" (primary adult). A dev-only member
 * picker lives in the topnav so the operator can switch perspective.
 */
import { useEffect, useState } from 'react'
import { useApp } from './store'
import { getSession, onAuthStateChange } from './auth'
import { fetchMembers } from './db'
import { SEED_MEMBERS } from './seed'
import { AuthScreen } from './surfaces/AuthScreen'
import { WorkspaceShell } from './surfaces/WorkspaceShell'
import { Sparkles } from 'lucide-react'

export function App() {
  const { session, setSession, setCurrentMember, currentMember, skin, setSkin, dev } = useApp()
  const [authReady, setAuthReady] = useState(false)
  const [memberReady, setMemberReady] = useState(false)

  // DEV MODE: skip auth, drop straight into the workspace with a seed member.
  useEffect(() => {
    if (dev) {
      // Use a microtask to defer the state updates so they don't fire synchronously
      // inside the effect (which can cause dev-mode double-invocation loops in StrictMode)
      queueMicrotask(() => {
        setAuthReady(true)
        setSession({ userId: 'dev-mom' })
        // Only seed the member if persist hasn't already loaded one
        if (!currentMember) setCurrentMember(SEED_MEMBERS[0])
        setMemberReady(true)
      })
      return
    }
    getSession().then(({ data }) => {
      if (data.session?.user) {
        setSession({ userId: data.session.user.id })
      }
      setAuthReady(true)
    })
    const subPromise = onAuthStateChange((_event, newSession) => {
      if (newSession?.user) {
        setSession({ userId: newSession.user.id })
      } else {
        setSession(null)
        setCurrentMember(null)
      }
    })
    return () => {
      subPromise.then((sub: any) => {
        try { sub?.data?.subscription?.unsubscribe() } catch {}
      })
    }
  }, [dev, setSession, setCurrentMember])

  // Once signed in, load the current member (skipped in dev mode)
  useEffect(() => {
    if (dev) return
    if (!session) {
      setMemberReady(false)
      return
    }
    fetchMembers().then(({ data: members }) => {
      const me = members?.find((m) => m.id === session.userId)
      setCurrentMember(me ?? null)
      setMemberReady(true)
    })
  }, [dev, session, setCurrentMember])

  // Apply skin to root
  useEffect(() => {
    document.documentElement.dataset.skin = skin
  }, [skin])

  if (!authReady) {
    return <Splash />
  }

  if (!dev && !session) {
    return <AuthScreen />
  }

  if (!dev && !memberReady) {
    return <Splash />
  }

  return <WorkspaceShell />
}

function Splash() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-skin-bg text-skin-text">
      <div className="flex items-center gap-2 text-slate-500">
        <Sparkles className="w-5 h-5 animate-pulse text-garden-600" />
        <span>Loading…</span>
      </div>
    </div>
  )
}
